import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Plus, Trash2, ChevronUp, ChevronDown, Upload, User } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { RichTextEditor } from '../../RichTextEditor';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { ButtonsEditor } from '../shared/ButtonsEditor';
import type { ButtonData } from '../shared/ButtonsEditor';
import { TestimonialUploadModal } from '../../TestimonialUploadModal';

interface TestimonialItem {
  id: string;
  name: string;
  role: string;
  company: string;
  quote: string;
  rating: number;
  avatarUrl: string;
  avatarCloudfrontUrl?: string;
  avatarMediaId?: string;
  order: number;
}

interface TestimonialsData {
  title?: string;
  subtitle?: string;
  layout?: string;
  showRating?: boolean;
  showAvatar?: boolean;
  showCompany?: boolean;
  items?: TestimonialItem[];
  customColors?: {
    background?: string;
    title?: string;
    cardBackground?: string;
    quoteText?: string;
    nameText?: string;
    starColor?: string;
  };
  buttons?: ButtonData[];
}

interface TestimonialsEditorProps {
  formData: TestimonialsData;
  setFormData: (data: TestimonialsData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
  siteId: string;
}

export const TestimonialsEditor: React.FC<TestimonialsEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  currentPageComponents,
  siteId,
}) => {
  const data = formData;
  const items = data.items || [];
  const buttons = data.buttons || [];
  const themeColors = useCustomColors();

  // Modal state for avatar upload
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [editingTestimonialId, setEditingTestimonialId] = useState<string | null>(null);
  const [editingTestimonialName, setEditingTestimonialName] = useState('');

  const addTestimonialItem = () => {
    const newItem: TestimonialItem = {
      id: uuidv4(),
      name: '',
      role: '',
      company: '',
      quote: '',
      rating: 5,
      avatarUrl: '',
      order: items.length,
    };
    setFormData({ ...data, items: [...items, newItem] });
  };

  const updateTestimonialItem = (index: number, field: string, value: any) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setFormData({ ...data, items: updatedItems });
  };

  const removeTestimonialItem = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    updatedItems.forEach((item, i) => {
      item.order = i;
    });
    setFormData({ ...data, items: updatedItems });
  };

  const moveTestimonialItem = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === items.length - 1)
    ) {
      return;
    }
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const updatedItems = [...items];
    [updatedItems[index], updatedItems[newIndex]] = [updatedItems[newIndex], updatedItems[index]];
    updatedItems.forEach((item, i) => {
      item.order = i;
    });
    setFormData({ ...data, items: updatedItems });
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Avatar upload handlers
  const openAvatarUpload = (testimonialId: string, testimonialName: string) => {
    setEditingTestimonialId(testimonialId);
    setEditingTestimonialName(testimonialName);
    setIsUploadModalOpen(true);
  };

  const handleAvatarUploaded = (imageUrl: string, cloudfrontUrl?: string, mediaId?: string) => {
    if (!editingTestimonialId) return;

    const itemIndex = items.findIndex(item => item.id === editingTestimonialId);
    if (itemIndex !== -1) {
      const updatedItems = [...items];
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        avatarUrl: cloudfrontUrl || imageUrl,
        avatarCloudfrontUrl: cloudfrontUrl,
        avatarMediaId: mediaId,
      };
      setFormData({ ...data, items: updatedItems });
    }

    setEditingTestimonialId(null);
    setEditingTestimonialName('');
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('testimonials', themeColors);

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
        <input
          type="text"
          value={data.title || ''}
          onChange={e => setFormData({ ...data, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="What Our Clients Say"
        />
      </div>

      {/* Subtitle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Section Subtitle</label>
        <RichTextEditor
          content={data.subtitle || ''}
          onChange={value => setFormData({ ...data, subtitle: value })}
          placeholder="Add a description for your testimonials section..."
        />
      </div>

      {/* Layout Options */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Layout Style</label>
          <select
            value={data.layout || 'grid'}
            onChange={e => setFormData({ ...data, layout: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="grid">Grid (3 Columns)</option>
            <option value="carousel">Carousel</option>
            <option value="list">List</option>
          </select>
        </div>
      </div>

      {/* Toggle Options */}
      <ToggleOptions data={data} setFormData={setFormData} />

      {/* Testimonial Items */}
      <TestimonialItemsSection
        items={items}
        onAddItem={addTestimonialItem}
        onUpdateItem={updateTestimonialItem}
        onRemoveItem={removeTestimonialItem}
        onMoveItem={moveTestimonialItem}
        onOpenAvatarUpload={openAvatarUpload}
      />

      {/* Avatar Upload Modal */}
      <TestimonialUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false);
          setEditingTestimonialId(null);
          setEditingTestimonialName('');
        }}
        siteId={siteId}
        testimonialId={editingTestimonialId || ''}
        testimonialName={editingTestimonialName}
        currentImageUrl={
          editingTestimonialId
            ? items.find(item => item.id === editingTestimonialId)?.avatarUrl
            : undefined
        }
        onImageUploaded={handleAvatarUploaded}
      />

      {/* Call-to-Action Buttons */}
      <ButtonsEditor
        buttons={buttons}
        onChange={newButtons => setFormData({ ...data, buttons: newButtons })}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        label="Call-to-Action Buttons (Optional)"
      />

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Color Customization"
        description="Customize testimonials section colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
        onClearAllColors={() => setFormData({ ...data, customColors: undefined })}
      />
    </div>
  );
};

// Sub-components

interface ToggleOptionsProps {
  data: TestimonialsData;
  setFormData: (data: TestimonialsData) => void;
}

const ToggleOptions: React.FC<ToggleOptionsProps> = ({ data, setFormData }) => (
  <div className="grid grid-cols-3 gap-4">
    <label className="flex items-center space-x-2">
      <input
        type="checkbox"
        checked={data.showRating !== false}
        onChange={e => setFormData({ ...data, showRating: e.target.checked })}
        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      <span className="text-sm text-gray-700">Show Rating</span>
    </label>

    <label className="flex items-center space-x-2">
      <input
        type="checkbox"
        checked={data.showAvatar !== false}
        onChange={e => setFormData({ ...data, showAvatar: e.target.checked })}
        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      <span className="text-sm text-gray-700">Show Avatar</span>
    </label>

    <label className="flex items-center space-x-2">
      <input
        type="checkbox"
        checked={data.showCompany !== false}
        onChange={e => setFormData({ ...data, showCompany: e.target.checked })}
        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      <span className="text-sm text-gray-700">Show Company</span>
    </label>
  </div>
);

interface TestimonialItemsSectionProps {
  items: TestimonialItem[];
  onAddItem: () => void;
  onUpdateItem: (index: number, field: string, value: any) => void;
  onRemoveItem: (index: number) => void;
  onMoveItem: (index: number, direction: 'up' | 'down') => void;
  onOpenAvatarUpload: (testimonialId: string, testimonialName: string) => void;
}

const TestimonialItemsSection: React.FC<TestimonialItemsSectionProps> = ({
  items,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onMoveItem,
  onOpenAvatarUpload,
}) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <label className="block text-sm font-medium text-gray-700">
        Testimonials ({items.length})
      </label>
      <button
        type="button"
        onClick={onAddItem}
        className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100"
      >
        <Plus size={16} />
        <span>Add Testimonial</span>
      </button>
    </div>

    <div className="space-y-4">
      {items.map((item, index) => (
        <TestimonialItemCard
          key={item.id}
          item={item}
          index={index}
          totalItems={items.length}
          onUpdate={(field, value) => onUpdateItem(index, field, value)}
          onRemove={() => onRemoveItem(index)}
          onMove={direction => onMoveItem(index, direction)}
          onOpenAvatarUpload={onOpenAvatarUpload}
        />
      ))}

      {items.length === 0 && (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <p className="mb-2">No testimonials yet</p>
          <button
            type="button"
            onClick={onAddItem}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Add your first testimonial
          </button>
        </div>
      )}
    </div>
  </div>
);

interface TestimonialItemCardProps {
  item: TestimonialItem;
  index: number;
  totalItems: number;
  onUpdate: (field: string, value: any) => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
  onOpenAvatarUpload: (testimonialId: string, testimonialName: string) => void;
}

const TestimonialItemCard: React.FC<TestimonialItemCardProps> = ({
  item,
  index,
  totalItems,
  onUpdate,
  onRemove,
  onMove,
  onOpenAvatarUpload,
}) => {
  const avatarUrl = item.avatarCloudfrontUrl || item.avatarUrl;

  return (
    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex items-start justify-between mb-3">
        <span className="text-sm font-medium text-gray-500">Testimonial {index + 1}</span>
        <div className="flex items-center space-x-1">
          <button
            type="button"
            onClick={() => onMove('up')}
            disabled={index === 0}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            <ChevronUp size={16} />
          </button>
          <button
            type="button"
            onClick={() => onMove('down')}
            disabled={index === totalItems - 1}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            <ChevronDown size={16} />
          </button>
          <button type="button" onClick={onRemove} className="p-1 text-red-400 hover:text-red-600">
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {/* Avatar Upload */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">Avatar (optional)</label>
          <div className="flex items-center space-x-4">
            {avatarUrl ? (
              <div className="relative group">
                <img
                  src={avatarUrl}
                  alt={`${item.name || 'Person'}'s avatar`}
                  className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-full transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={() =>
                      onOpenAvatarUpload(item.id, item.name || `Testimonial ${index + 1}`)
                    }
                    className="p-1 bg-white rounded-full shadow"
                  >
                    <Upload size={14} className="text-gray-600" />
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => onOpenAvatarUpload(item.id, item.name || `Testimonial ${index + 1}`)}
                className="w-16 h-16 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-all group"
              >
                <User size={24} className="text-gray-400 group-hover:text-primary-500" />
              </div>
            )}
            <div className="flex-1">
              <button
                type="button"
                onClick={() => onOpenAvatarUpload(item.id, item.name || `Testimonial ${index + 1}`)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                {avatarUrl ? 'Change photo' : 'Upload photo'}
              </button>
              <p className="text-xs text-gray-500 mt-1">
                {avatarUrl ? 'Click to replace' : 'A default avatar will be shown if empty'}
              </p>
            </div>
          </div>
        </div>

        {/* Quote */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Quote</label>
          <RichTextEditor
            content={item.quote || ''}
            onChange={value => onUpdate('quote', value)}
            placeholder="Enter the testimonial quote..."
          />
        </div>

        {/* Name and Role */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
            <input
              type="text"
              value={item.name || ''}
              onChange={e => onUpdate('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Role</label>
            <input
              type="text"
              value={item.role || ''}
              onChange={e => onUpdate('role', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="CEO"
            />
          </div>
        </div>

        {/* Company and Rating */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Company</label>
            <input
              type="text"
              value={item.company || ''}
              onChange={e => onUpdate('company', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Company Name"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Rating (1-5)</label>
            <select
              value={item.rating || 5}
              onChange={e => onUpdate('rating', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value={5}>5 Stars</option>
              <option value={4}>4 Stars</option>
              <option value={3}>3 Stars</option>
              <option value={2}>2 Stars</option>
              <option value={1}>1 Star</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};
